import { useTranslation } from 'react-i18next';
import type { MissedStep } from './note-save';
import { problemOf } from './NoteFileParts';

/** The projects' and client's refusals (#915 C3), named in words under `save.problems`. */
const PARITY_PROBLEMS = [
  'TOO_MANY_PROJECTS',
  'PROJECT_REPEATED',
  'PROJECT_SHARES_NOT_100',
  'PERCENT_FORMAT',
  'PROJECT_NOT_ACTIVE',
  'CLIENT_NOT_ACTIVE',
  'CATALOG_UNAVAILABLE',
] as const;
export type ParityProblem = (typeof PARITY_PROBLEMS)[number];

export function parityProblem(code: string): ParityProblem | null {
  return PARITY_PROBLEMS.find((known) => known === code) ?? null;
}

export function NoteSaveProblem({ problem }: { readonly problem: ParityProblem | null }) {
  const { t } = useTranslation('expenses-parity');
  if (problem === null) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {t(`save.problems.${problem}`)}
    </p>
  );
}

/**
 * The note is saved, but not all of it: what is missing and why, and that «Salva» tries again
 * (coordinator, 1 Nov: the note stays saved with a clear message).
 */
export function NoteSaveMissed({ missed }: { readonly missed: readonly MissedStep[] }) {
  const { t } = useTranslation('expenses-parity');
  if (missed.length === 0) return null;
  return (
    <div
      role="alert"
      className="border-destructive flex flex-col gap-1 rounded-md border p-3 text-sm"
    >
      <b>{t('save.partial')}</b>
      <ul className="list-disc pl-5">
        {missed.map((step) => (
          <li key={stepKey(step)}>{lineFor(t, step)}</li>
        ))}
      </ul>
      <span>{t('save.retry')}</span>
    </div>
  );
}

function stepKey(step: MissedStep): string {
  if (step.kind === 'allocation') return 'allocation';
  return step.kind === 'upload' ? `u-${step.key}` : `r-${step.documentId}`;
}

function lineFor(
  t: ReturnType<typeof useTranslation<'expenses-parity'>>['t'],
  step: MissedStep,
): string {
  if (step.kind === 'allocation') {
    const problem = parityProblem(step.code);
    return t('save.missedAllocation', {
      why: problem === null ? t('save.problems.other') : t(`save.problems.${problem}`),
    });
  }
  const why = t(`files.problems.${problemOf(step.code)}`);
  return step.kind === 'upload'
    ? t('save.missedUpload', { name: step.name, why })
    : t('save.missedRemove', { why });
}
